import Collaboration from '@tiptap/extension-collaboration';
import {getApp} from 'firebase/app';
import {getAuth} from 'firebase/auth';
import {useEffect, useRef} from 'react';
import {useAuthState} from 'react-firebase-hooks/auth';
import {editorExtensions} from './EditorExtensions';
import {EditorContent, useEditor, type Editor, type UseEditorOptions} from '@tiptap/react';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import {randomUserColor} from './TipTapUtil';
import {useEditorNavigation} from './hooks/useEditorNavigation';
import {handleDrop} from './hooks/handleDrop';
import {CSSProperties} from 'react';
import type {JSONObject} from 'types/common';
import type {TipTapDocument} from './tipTapDocument';
// a map from file id to link
export const fileMap = new Map();

export interface TipTapButtonBarProps {
  editor: Editor;
  editable: boolean;
}

export interface TipTapEditorProps {
  provider: any;
  onUpdate: (json: JSONObject, html: string, text: string) => void;
  editable: boolean;
  collaborative: boolean;
  serverData: TipTapDocument;
  buttonBar: (props?: TipTapButtonBarProps) => React.ReactNode | void;
  height?: number;
  firebaseDocPath: string;
  onCreate?: (json: JSONObject, html: string, text: string) => void;
  style?: any;
}

/**
 * This is the actual editor
 */
export const TipTapEditor = ({
  provider,
  onUpdate,
  editable,
  collaborative,
  serverData,
  buttonBar,
  height,
  firebaseDocPath,
  onCreate,
  style
}: TipTapEditorProps) => {
  const [user] = useAuthState(getAuth(getApp()));

  const editorRef = useRef(null);

  let extensions = editorExtensions;
  if (collaborative) {
    const colabExtensions = [
      Collaboration.configure({
        document: provider ? provider.doc : null,
      }),

      CollaborationCursor.configure({
        provider: provider,
        user: {
          name: user?.displayName ?? 'Anonymous',
          color: randomUserColor(),
          // this does not update, but it is a promising idea...
          editable: editable,
        },
      }),
    ];
    // @ts-ignore
    // there is a type script error about extension type and colaboration type
    extensions = [...extensions, ...colabExtensions];
  }

  const editorParams: UseEditorOptions = {
    extensions: extensions,
    editorProps: {},
    editable: editable,
  };

  if (!collaborative) {
    // there are two ways to injest data, via a YJS provider
    // or via setting the content field. We use the later
    // if and only if we are not using YJS
    if (!serverData || !serverData.content) {
      editorParams.content = '';
    } else {
      editorParams.content = serverData.content;
    }
  }
  const editor = useEditor(editorParams);

  useEditorNavigation(editor);

  useEffect(() => {
    const assignChildrenIds = (parent, prefix) => {
      if (!parent) return;
      const children = parent.children;
      if (!children || children.length === 0) {
        return;
      }
      for (let i = 0; i < children.length; i++) {
        if (!children[i].id) {
          children[i].id = prefix + '-' + i;
        }
        if (children[i].children.length > 0) {
          assignChildrenIds(children[i], prefix + '-' + i);
        }
      }
    };

    const parentElement = document.getElementById('tiptap-editor-container'); // Select the parent element
    assignChildrenIds(parentElement, `tiptap-${firebaseDocPath}`); // Assign the ids
  }, [editor]);

  useEffect(() => {
    // this is a hack, but it is one I ran through the discussion
    // forum of tiptap. I believe since I brought it up they
    // may have fixed it so that the editable field of the editor
    // is reactive (children can listed to it)
    if (editor) {
      editor.setEditable(editable);
    }
    if (collaborative) {
      // update the user's editable status
    }
  }, [editable]);

  useEffect(() => {
    if (!editor) return;
    editorRef.current = editor;
    editor.on('update', ({editor}) => {
      let json = editor.getJSON();
      let html = editor.getHTML();
      let text = editor.getText();
      onUpdate(json, html, text);
    });
    editor.on('create', ({editor}) => {
      if (!onCreate) return;
      let json = editor.getJSON();
      let html = editor.getHTML();
      let text = editor.getText();
      onCreate(json, html, text);
    });
  }, [editor]);

  let tiptapStyle: CSSProperties = outerStyle;
  if (height) {
    tiptapStyle = {
      ...style,
      ...outerStyle,
      height: height,
    };
  } else if (style) {
    tiptapStyle = {
      ...outerStyle,
      ...style,
    };
  }

  // Handle drag-and-drop
  const handleDragOver = e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  return (
    <div
      style={tiptapStyle}
      onDragOver={handleDragOver}
      onDrop={e => handleDrop(e, editor, editable)}
    >
      {buttonBar && (buttonBar({editor, editable}) || <></>)}
      {/* classname editor is the parent to prosemirror styles */}
      <div
        className={editable ? 'editor' : ''}
        style={editable ? innerStyle : {}}
        onClick={() => {
          if (editable) {
            // Bug: this didn't work (it kept stealing focus from ace editor)
            // if (!editor.view.hasFocus()) {
            //   editor.chain().focus().run();
            // }
          }
        }}
        id="tiptap-editor-container"
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

const outerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

const innerStyle: CSSProperties = {
  padding: '10px',
  flex: 1,
  overflow: 'auto',
};
